/**
 * Connection Manager - Gerencia conexões WebSocket por aba/navegador
 * 
 * ARQUITETURA:
 * - Uma conexão WebSocket por ABA do navegador (comportamento padrão do Laravel Echo)
 * - Múltiplas subscriptions (chats) na mesma conexão dentro de cada aba
 * - Computadores diferentes = conexões diferentes (naturalmente)
 * - Web-chat e Web-admin = conexões diferentes (aplicações diferentes)
 * 
 * POR QUE MÚLTIPLAS CONEXÕES É MELHOR:
 * ✅ Isolamento: Abas independentes (uma crash não afeta outras)
 * ✅ Simplicidade: Não precisa sincronização complexa
 * ✅ Segurança: Cada conexão autenticada individualmente
 * ✅ Performance: WebSocket é leve (~5KB por conexão)
 * 
 * Características:
 * - Múltiplos chats na mesma aba = mesma conexão (otimizado)
 * - Diferentes abas = diferentes conexões (comportamento padrão)
 * - Cleanup automático quando não há mais subscriptions
 * - Reutilização de conexão entre diferentes chats na mesma aba
 */

import Echo from 'laravel-echo';
import { getEcho, initLaravelEcho } from './laravelEchoService';

// Mapa de subscriptions ativas: conversationId -> unsubscribe function
const activeSubscriptions = new Map<number, () => void>();

// Contador de referências por conversa (para permitir múltiplas janelas com mesmo chat)
const subscriptionRefs = new Map<number, number>();

// Mapa de intervals de ping: conversationId -> interval
const pingIntervals = new Map<number, NodeJS.Timeout>();

/**
 * Inscrever-se em uma conversa usando a conexão compartilhada
 * Se já houver uma conexão, reutiliza. Se não, cria uma nova.
 * 
 * @param conversationId ID da conversa
 * @param callback Callback para receber mensagens
 * @param userId ID do usuário (opcional, será buscado via API se não fornecido)
 */
export const subscribeToConversation = (
  conversationId: number,
  callback: (message: any) => void,
  userId?: number | null
): (() => void) => {
  console.log(`[ConnectionManager] Inscrevendo-se na conversa ${conversationId}`);
  
  // Incrementar contador de referências
  const currentRefs = subscriptionRefs.get(conversationId) || 0;
  subscriptionRefs.set(conversationId, currentRefs + 1);
  
  // Se já existe subscription ativa, apenas adicionar callback
  if (activeSubscriptions.has(conversationId)) {
    console.log(`[ConnectionManager] Conversa ${conversationId} já está inscrita, incrementando referências`);
    // Retornar função de unsubscribe que decrementa referências
    return () => {
      const refs = subscriptionRefs.get(conversationId) || 0;
      if (refs <= 1) {
        // Última referência, remover subscription
        const unsub = activeSubscriptions.get(conversationId);
        if (unsub) {
          unsub();
          activeSubscriptions.delete(conversationId);
          subscriptionRefs.delete(conversationId);
          console.log(`[ConnectionManager] Última referência removida, desinscrevendo da conversa ${conversationId}`);
        }
      } else {
        // Ainda há outras referências, apenas decrementar
        subscriptionRefs.set(conversationId, refs - 1);
        console.log(`[ConnectionManager] Referência removida, ainda há ${refs - 1} referências ativas para conversa ${conversationId}`);
      }
    };
  }
  
  // Inicializar Laravel Echo se ainda não foi inicializado
  // Isso cria a conexão WebSocket compartilhada (apenas UMA vez)
  let echo: Echo<any>;
  try {
    echo = getEcho();
  } catch (error) {
    // Se getEcho falhar, tentar inicializar explicitamente
    console.log('[ConnectionManager] Inicializando Laravel Echo...');
    echo = initLaravelEcho();
  }
  
  // SEGURANÇA: Clientes usam PRIVATE channel (não presence)
  // Apenas admins podem usar presence channels para ver quem está online
  // Private channels não expõem lista de usuários conectados
  const channel = echo.private(`conversation.${conversationId}`);
  
  // Escutar evento de mensagem
  channel.listen('.message.sent', (data: any) => {
    console.log(`[ConnectionManager] Mensagem recebida na conversa ${conversationId}:`, data);
    const message = data.message || data;
    callback(message);
  });
  
  // Escutar eventos de conexão para debug
  channel.listen('pusher:subscription_succeeded', () => {
    console.log(`[ConnectionManager] ✅ Inscrito com sucesso no canal private-conversation.${conversationId}`);
  });
  
  channel.listen('pusher:subscription_error', (error: any) => {
    console.error(`[ConnectionManager] ❌ Erro ao se inscrever no canal private-conversation.${conversationId}:`, error);
  });
  
  // PING: Enviar ping a cada 30 segundos para indicar que está online
  // Apenas quando chat está aberto (quando está inscrito no canal)
  // pingIntervals é declarado no escopo global para gerenciar todos os intervals
  
  // Obter user_id do usuário atual
  // Usar userId fornecido ou buscar via API
  const getCurrentUserId = async (): Promise<number | null> => {
    // Se userId foi fornecido, usar ele
    if (userId) {
      return userId;
    }
    
    // Caso contrário, buscar via API
    try {
      const API_URL = process.env.REACT_APP_API_URL || window.location.origin + '/api';
      const response = await fetch(`${API_URL}/me`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.user?.id || data.user_id || null;
      }
    } catch (e) {
      console.warn('[ConnectionManager] Erro ao obter user_id via API:', e);
    }
    return null;
  };
  
  // Iniciar ping quando se inscreve com sucesso
  channel.subscribed(async () => {
    console.log(`[ConnectionManager] ✅ Inscrição bem-sucedida no canal private-conversation.${conversationId}`);
    
    const userId = await getCurrentUserId();
    if (userId) {
      console.log(`[ConnectionManager] Iniciando ping para usuário ${userId} na conversa ${conversationId}`);
      
      // Função para enviar ping via whisper (direto pelo WebSocket - mais eficiente)
      const sendPing = (currentUserId: number) => {
        try {
          // Verificar se o canal está pronto
          if (!channel) {
            console.warn(`[ConnectionManager] ⚠️ Canal não disponível para whisper`);
            return;
          }
          
          console.log(`[ConnectionManager] 📤 Enviando ping via whisper para usuário ${currentUserId}`);
          console.log(`[ConnectionManager] 📤 Canal: ${channel.name}`);
          console.log(`[ConnectionManager] 📤 Dados:`, {
            user_id: currentUserId,
            conversation_id: conversationId,
            timestamp: Date.now(),
          });
          
          // Enviar whisper no canal privado (admin também está neste canal)
          channel.whisper('user.ping', {
            user_id: currentUserId,
            conversation_id: conversationId,
            timestamp: Date.now(),
          });
          
          console.log(`[ConnectionManager] ✅ Whisper enviado com sucesso`);
        } catch (error) {
          console.error(`[ConnectionManager] ❌ Erro ao enviar ping via whisper:`, error);
        }
      };
      
      // Enviar ping imediatamente
      sendPing(userId);
      
      // Armazenar userId para usar no interval
      let currentUserId = userId;
      
      // Configurar ping periódico a cada 30 segundos
      const interval = setInterval(() => {
        if (currentUserId) {
          sendPing(currentUserId);
        } else {
          console.warn(`[ConnectionManager] Não foi possível obter user_id para ping, parando intervalo`);
          clearInterval(interval);
          pingIntervals.delete(conversationId);
        }
      }, 30000); // 30 segundos
      
      // Armazenar interval para poder limpar depois
      pingIntervals.set(conversationId, interval);
    } else {
      console.warn(`[ConnectionManager] Não foi possível obter user_id, ping não será enviado`);
    }
  });
  
  // Criar função de unsubscribe
  const unsubscribe = () => {
    console.log(`[ConnectionManager] Desinscrevendo da conversa ${conversationId}`);
    
    // Parar ping quando desinscreve (evita gargalos)
    const interval = pingIntervals.get(conversationId);
    if (interval) {
      console.log(`[ConnectionManager] Parando ping para conversa ${conversationId}`);
      clearInterval(interval);
      pingIntervals.delete(conversationId);
    }
    
    channel.stopListening('.message.sent');
    echo.leave(`conversation.${conversationId}`); // Laravel Echo remove automaticamente o prefixo "private-"
    activeSubscriptions.delete(conversationId);
    subscriptionRefs.delete(conversationId);
  };
  
  // Salvar subscription
  activeSubscriptions.set(conversationId, unsubscribe);
  
  console.log(`[ConnectionManager] ✅ Subscription criada para conversa ${conversationId}`);
  console.log(`[ConnectionManager] Total de subscriptions ativas: ${activeSubscriptions.size}`);
  
  // Retornar função de unsubscribe que gerencia referências
  return () => {
    const refs = subscriptionRefs.get(conversationId) || 0;
    if (refs <= 1) {
      // Última referência, remover subscription
      unsubscribe();
    } else {
      // Ainda há outras referências, apenas decrementar
      subscriptionRefs.set(conversationId, refs - 1);
      console.log(`[ConnectionManager] Referência removida, ainda há ${refs - 1} referências ativas para conversa ${conversationId}`);
    }
  };
};

/**
 * Obter lista de usuários online no canal
 * NOTA: Esta função não funciona para clientes (private channels não expõem membros)
 * Apenas admins podem ver lista de usuários online usando presence channels
 */
export const getOnlineUsers = (conversationId: number): any[] => {
  // Clientes usam private channels que não expõem lista de membros
  // Esta funcionalidade está disponível apenas para admins via presence channels
  console.warn('[ConnectionManager] getOnlineUsers() não está disponível para clientes. Apenas admins podem ver usuários online.');
  return [];
};

/**
 * Obter estatísticas da conexão
 */
export const getConnectionStats = () => {
  return {
    activeSubscriptions: activeSubscriptions.size,
    subscriptions: Array.from(activeSubscriptions.keys()),
    refs: Object.fromEntries(subscriptionRefs),
  };
};

/**
 * Limpar todas as subscriptions (útil para logout)
 */
export const clearAllSubscriptions = () => {
  console.log(`[ConnectionManager] Limpando todas as ${activeSubscriptions.size} subscriptions`);
  
  // Parar todos os pings
  pingIntervals.forEach((interval, conversationId) => {
    console.log(`[ConnectionManager] Parando ping para conversa ${conversationId}`);
    clearInterval(interval);
  });
  pingIntervals.clear();
  
  activeSubscriptions.forEach((unsub) => unsub());
  activeSubscriptions.clear();
  subscriptionRefs.clear();
};
